import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { MainLayout } from "./layout/MainLayout";
import { canAccessRoute } from "../utils/permissions";

/**
 * ProtectedRoute Component
 * Handles authentication and permission-based route protection
 *
 * Features:
 * - Redirects to login if not authenticated
 * - Checks route permissions if specified
 * - Shows loading state during auth check
 * - Wraps content in MainLayout
 */
export const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check route permissions
  const hasAccess = canAccessRoute(user, location.pathname);

  console.log("🔒 [ProtectedRoute] Access check:", {
    path: location.pathname,
    hasAccess,
    userEmail: user?.email,
    isSuperAdmin: user?.isSuperAdmin,
    isSuperAdminType: typeof user?.isSuperAdmin,
    permissionCount: user?.permissionCodes?.length || 0,
    userObject: user ? {
      isSuperAdmin: user.isSuperAdmin,
      email: user.email,
      permissionCodesPreview: user.permissionCodes?.slice(0, 3)
    } : null
  });

  // Explicit Super admin bypass - double check with multiple conditions
  if (user?.isSuperAdmin === true || user?.isSuperAdmin === "true" || user?.email === "admin@metatalio.com") {
    console.log("✅ [ProtectedRoute] Super Admin bypass granted for:", location.pathname, "User:", user?.email);
    return (
      <MainLayout>
        <Outlet />
      </MainLayout>
    );
  }

  if (!hasAccess) {
    console.warn("❌ [ProtectedRoute] Access denied to:", location.pathname, "for user:", user?.email);
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <MainLayout>
      <Outlet />
    </MainLayout>
  );
};
